import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Stack,
  Button,
  Container,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';

import { getAdmins, toggleCompanyStatus } from 'services/requests/superuser';
import { setMask, toast } from 'utils';

import Breadcrumbs from 'components/Breadcrumbs';
import Iconify from 'components/Iconify';
import List from 'components/List2';
import CreateAdminForm from 'sections/dialogs/CreateAdminForm';

export default function AdminsList() {
  const listRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleToggleStatus = async (e, companyId, currentStatus) => {
    e.stopPropagation();
    
    try {
      await toggleCompanyStatus(companyId);
      toast(`Empresa ${currentStatus ? 'desativada' : 'ativada'} com sucesso`, 'success');
      if (listRef.current) {
        listRef.current.refreshData();
      }
    } catch (error) {
      toast(error, 'error');
    }
  };

  const listConfig = {
    placeholderFilter: 'Buscar...',
    onClick: () => null,
    fields: [
      {
        key: 'company',
        label: 'Empresa',
        desktopColumnSize: 3,
        customValue: row => row.company?.name || '-'
      },
      { key: 'name', label: 'Nome', desktopColumnSize: 2, isPrimary: true },
      { key: 'email', label: 'Email', desktopColumnSize: 3 },
      {
        key: 'phone',
        label: 'Telefone',
        desktopColumnSize: 2,
        customValue: row => (
          <>
            <a href={`https://wa.me/+55${row.phone}`} target="_blank" rel="noreferrer">{setMask('(99) 99999-9999', row.phone)}</a>
            <Iconify icon="mingcute:whatsapp-fill" width={15} sx={{ ml: 1 }} />
          </>
        )
      },
      {
        key: 'status',
        label: '',
        desktopColumnSize: 2,
        customValue: row => {
          const isActive = row.company?.active !== false;
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              mt={{ xs: 1, md: 0 }}
              spacing={1}
            >
              <Chip
                label={isActive ? 'Ativa' : 'Desativada'}
                size="small"
                color={isActive ? 'success' : 'error'}
                variant="outlined"
              />
              <Tooltip title={isActive ? 'Desativar empresa' : 'Ativar empresa'}>
                <IconButton
                  size="small"
                  onClick={(e) => handleToggleStatus(e, row.company?.id, isActive)}
                  color={isActive ? 'error' : 'success'}
                >
                  <Iconify icon="mdi:power" width={24} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }
      },
    ],
    fetchData: async ({ page, searchTerm }) => {
      const response = await getAdmins({ page, searchTerm, paginationDisabled: false });
      return {
        data: response.data || [],
        pagination: response.pagination || {}
      };
    },
  };

  return (
    <>
      <Helmet>
        <title>Administradores | CredPah</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Breadcrumbs
            noback
            items={[
              { label: 'Empresas Provedoras de Crédito' }
            ]}
          />

          {
            !process.env.REACT_APP_ADD_COMPANY_DISABLED && ( // credpress only
              <Button
                sx={{ px: 2 }}
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" width={20} />}
                onClick={() => setIsCreating(true)}
              >
                Empresa
              </Button>
            )
          }
        </Stack>

        <List
          getData={(params) => getAdmins({ ...params })}
          ref={listRef}
          config={listConfig}
        />

        <CreateAdminForm
          open={isCreating}
          onCancel={() => setIsCreating(false)}
          onSuccess={() => {
            setIsCreating(false);
            if (listRef.current) {
              listRef.current.refreshData();
            }
          }}
        />
      </Container>
    </>
  );
}

